import Immutable from 'immutable';
import React, { useContext } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { useDispatch, useSelector } from 'react-redux';

import Context from '@bibliocommons/context';

import { trackContentClick, trackContentExplore, trackContentImpression } from 'app/actions/AnalyticsGa4Actions';
import { UI_COMPONENT_TYPE, UI_CONTAINER_TITLE, UI_CONTAINER_TYPE } from 'app/constants/AnalyticsGa4Constants';
import mapPromoRecommendationTypeToProperty from 'app/helpers/analytics/mapPromoRecommendationTypeToProperty';
import cardShape from 'app/shapes/cardShape';
import { selectCardSearchParameters } from 'app/selectors/CardsSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectPromoEntities } from 'app/selectors/EntitiesSelector';
import PromoCards from './PromoCards';

const hasDebugPermission = currentUser => {
  if (!currentUser) {
    return false;
  }

  const roles = currentUser.get('roles', Immutable.List());

  return roles.includes('LIBRARY_STAFF') || roles.includes('SYS_ADMIN');
};

const PromoCardsContainer = ({ promoCards }) => {
  const { mobileApp: isMobileApp } = useContext(Context);
  const dispatch = useDispatch();
  const cardSearchParameters = useSelector(selectCardSearchParameters);
  const currentLibrary = useSelector(selectCurrentLibrary);
  const currentUser = useSelector(selectCurrentUser);
  const promosById = useSelector(selectPromoEntities);

  const cmsUrl = currentLibrary.get('cmsUrl');
  const isDebugEnabled = hasDebugPermission(currentUser);

  const mapTrackingHandlerArgsToActionArgs = ({ card, position, uiComponentLabel = null, uiComponentType = null }) => {
    const cardId = card.get('id');
    const cardTitle = card.getIn(['title', 'text']);
    const cardType = (card.getIn(['contentType', 'slug']) ?? '').replace('-', '_');
    const promo = promosById.get(String(cardId));
    const impressionId = `${cardId}-${cardSearchParameters.get('requestId')}`;
    const recommendationType = promo?.get('recommendationType');

    return {
      card: {
        card_id: cardId,
        card_title: cardTitle,
        card_type: cardType
      },
      properties: {
        impression_id: impressionId,
        promotion_type: mapPromoRecommendationTypeToProperty(recommendationType)
      },
      ui: {
        ui_component_label: uiComponentLabel,
        ui_component_type: uiComponentType,
        ui_container_position: position,
        ui_container_title: UI_CONTAINER_TITLE.PERSONALIZED_PROMOS_MORE_FROM_LIBRARY,
        ui_container_type: UI_CONTAINER_TYPE.CARD
      }
    };
  };

  const trackCardImageClick = ({ card, position }) => {
    dispatch(
      trackContentClick(
        mapTrackingHandlerArgsToActionArgs({ card, position, uiComponentType: UI_COMPONENT_TYPE.LINK_IMAGE })
      )
    );
  };

  const trackCardImpression = ({ card, position }) => {
    dispatch(trackContentImpression(mapTrackingHandlerArgsToActionArgs({ card, position })));
  };

  const trackCardTitleClick = ({ card, linkText, position }) => {
    dispatch(
      trackContentClick(
        mapTrackingHandlerArgsToActionArgs({
          card,
          position,
          uiComponentLabel: linkText,
          uiComponentType: UI_COMPONENT_TYPE.LINK_HEADING
        })
      )
    );
  };

  const trackCardTypeClick = ({ card, linkText, position }) => {
    dispatch(
      trackContentExplore(
        mapTrackingHandlerArgsToActionArgs({
          card,
          position,
          uiComponentLabel: linkText,
          uiComponentType: UI_COMPONENT_TYPE.LINK_TEXT
        })
      )
    );
  };

  return (
    <PromoCards
      cmsUrl={cmsUrl}
      hasDiscoveryLinks={!isMobileApp}
      isDebugEnabled={isDebugEnabled}
      promoCards={promoCards}
      promosById={promosById}
      trackCardImageClick={trackCardImageClick}
      trackCardImpression={trackCardImpression}
      trackCardTitleClick={trackCardTitleClick}
      trackCardTypeClick={trackCardTypeClick}
    />
  );
};

PromoCardsContainer.propTypes = {
  promoCards: ImmutablePropTypes.listOf(cardShape).isRequired
};

export default PromoCardsContainer;
